import ICDelete from '@assets/jsx-icon/ICDelete';
import PopUpWarning from '@components/PopUpWarning';
import { cameraViewController, geoZoneController } from '@controllers/index';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GeoZones, GeoZoneType } from '@EcamModel/model';
import { CameraView } from '@EcamModel/model/CameraView';
import usePopUp from '@hooks/usePopUp';
import { ArrowDropUp, DragIndicator } from '@mui/icons-material';
import { Collapse, Divider, Fade, IconButton, Stack, Typography } from '@mui/material';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import TooltipDangerIconButton from '@pages/cameras/list/components/TooltipDangerIconButton';
import color from '@theme/Colors';
import _ from 'lodash';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { formatUKDate, formatUKTime } from 'src/helpers';

type Props = {
    id: number;
    cameraView: CameraView;
    collapse: () => void;
    listCollapse: { [key: string]: boolean };
    isDisabled: boolean;
};

type DeleteGzDetailProps = { geozoneId: number; viewOverviewId: number };

export default function GroupViewCameraPtzDnd(props: Props) {
    const { id, cameraView, listCollapse, collapse, isDisabled } = props;

    const queryClient = useQueryClient();

    const {
        selectedCameraView,
        setSelectedCameraView,
        listCameraView,
        setSelectedGeozone,
        handleDeleteGeoZonesOverView,
    } = useCameraDetailContext();

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const popUpDeleteCondition = usePopUp();

    const [deleteGzDetail, setDeleteGzDetail] = useState<DeleteGzDetailProps>();
    const [isLoadingSave, setIsLoadingSave] = useState(false);

    const geoZonesHasView = cameraView?.GeoZones?.map((gz, gzIndex) => ({ gz, gzIndex })).filter(
        ({ gz }) => gz.GeoZoneType !== GeoZoneType.Detail && !!gz.CameraViewDetail
    );

    const isOpen = listCollapse[`${id}`];

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        width: '100%',
    };

    const handleDeleteGzDetail = async () => {
        if (!deleteGzDetail) return;

        try {
            setIsLoadingSave(true);

            await handleDeleteGeoZonesOverView(deleteGzDetail?.geozoneId.toString());

            const cameraView = listCameraView.rows.find((e) => e.Id === deleteGzDetail.viewOverviewId);

            const updatedCameraView = cameraView
                ? {
                      ...cameraView,
                      GeoZones: (cameraView.GeoZones || [])
                          .map((gz) => ({
                              ...gz,
                              CameraViewDetail: gz.CameraViewDetail
                                  ? {
                                        ...gz.CameraViewDetail,
                                        GeoZones: (gz.CameraViewDetail.GeoZones || []).filter(
                                            (z) => z.Id !== deleteGzDetail?.geozoneId
                                        ),
                                    }
                                  : gz.CameraViewDetail,
                          }))
                          .filter((gz) => gz.Id !== deleteGzDetail?.geozoneId),
                  }
                : undefined;
            updatedCameraView && queryClient.invalidateQueries(['patrolTimeForecast']);

            updatedCameraView && setSelectedCameraView(updatedCameraView);
        } finally {
            setIsLoadingSave(false);
            popUpDeleteCondition.onClose();
        }
    };

    return (
        <Stack
            ref={setNodeRef}
            key={id}
            width={'100%'}
            bgcolor={color.white}
            borderRadius={2}
            spacing={2}
            p={2}
            style={style}
            border={`1px solid ${selectedCameraView?.Id === cameraView.Id ? color.success : color.grey300}`}
            padding={2}
            sx={{
                pointerEvents: isDisabled ? 'none' : 'auto',
                mt: 2,
            }}
        >
            <Stack
                direction={'row'}
                justifyContent={'space-between'}
                width={'100%'}
                alignItems={'center'}
                onClick={() => {
                    setSelectedCameraView(cameraView);
                }}
                borderRadius={2}
                sx={{
                    cursor: 'pointer',
                }}
            >
                <Stack direction={'row'} spacing={1} alignItems={'center'}>
                    <IconButton
                        {...attributes}
                        {...listeners}
                        sx={{
                            cursor: isDragging ? 'grabbing' : 'grab',
                        }}
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <DragIndicator fontSize="small" />
                    </IconButton>
                    <Stack spacing={1} justifyContent={'center'}>
                        <Stack direction={'row'} spacing={1}>
                            <Typography variant="h5">{cameraView?.CameraViewName}</Typography>
                        </Stack>
                        <Typography>Geo-zone count: {cameraView?.GeoZones?.length ?? 0}</Typography>
                        <Typography variant="body1" color={color.grey600} display={{ xs: 'none', sm: 'initial' }}>
                            Configured at {formatUKDate(cameraView?.Updated!)} on {formatUKTime(cameraView?.Updated!)}{' '}
                            by {!_.isNil(cameraView?.UpdatedBy) ? cameraView?.UpdatedBy : cameraView?.CreatedBy}
                        </Typography>
                    </Stack>
                </Stack>

                {!!geoZonesHasView?.length && (
                    <IconButton
                        sx={{ p: 0 }}
                        className="arrow-icon"
                        onClick={(event) => {
                            event.stopPropagation();
                            collapse();
                        }}
                    >
                        <ArrowDropUp
                            style={{
                                transition: 'all 0.3s',
                                transform: `rotate(${isOpen ? 0 : -180}deg)`,
                            }}
                        />
                    </IconButton>
                )}
            </Stack>

            <Collapse sx={{ width: '100%' }} in={!!geoZonesHasView?.length ? isOpen : false} unmountOnExit>
                <Fade in={!!geoZonesHasView?.length ? isOpen : false} timeout={500}>
                    <Stack width="100%" spacing={2}>
                        <Divider
                            orientation="horizontal"
                            sx={{
                                mx: 4,
                            }}
                        />
                        {geoZonesHasView?.map(({ gz, gzIndex }) => {
                            const cameraViewDetail = gz.CameraViewDetail;

                            return (
                                <Stack
                                    key={gz.Id}
                                    width={'100%'}
                                    onClick={() => {
                                        setSelectedCameraView(cameraViewDetail);
                                        setSelectedGeozone(gz);
                                    }}
                                    border={`1px solid ${
                                        selectedCameraView?.Id === cameraViewDetail?.Id ? color.success : color.grey300
                                    }`}
                                    borderRadius={2}
                                    padding={2}
                                    sx={{
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Stack direction={'row'} justifyContent={'space-between'} minHeight={'30px'}>
                                        <Typography variant="h5">
                                            {gz.GeoZoneName || cameraViewDetail?.CameraViewName}
                                        </Typography>

                                        <Stack spacing={2} direction={'row'}>
                                            {selectedCameraView?.Id === cameraViewDetail?.Id && (
                                                <TooltipDangerIconButton
                                                    icon={<ICDelete />}
                                                    tooltipTitle="Delete"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        popUpDeleteCondition.setTrue();
                                                        setDeleteGzDetail({
                                                            geozoneId: gz?.Id!,
                                                            viewOverviewId: cameraView?.Id!,
                                                        });
                                                    }}
                                                />
                                            )}
                                        </Stack>
                                    </Stack>

                                    <Stack spacing={1}>
                                        <Stack direction={'row'} spacing={1} justifyItems={'center'}>
                                            <Typography>View: {cameraViewDetail?.CameraViewName}</Typography>
                                        </Stack>

                                        <Typography
                                            variant="body1"
                                            color={color.grey600}
                                            display={{ xs: 'none', sm: 'initial' }}
                                        >
                                            Configured at {formatUKDate(cameraViewDetail?.Updated!)} on{' '}
                                            {formatUKTime(cameraViewDetail?.Updated!)} by{' '}
                                            {!_.isNil(cameraViewDetail?.UpdatedBy)
                                                ? cameraViewDetail?.UpdatedBy
                                                : cameraViewDetail?.CreatedBy}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            );
                        })}
                    </Stack>
                </Fade>
            </Collapse>

            <PopUpWarning
                {...popUpDeleteCondition}
                onConfirm={handleDeleteGzDetail}
                title="Confirm"
                message={'Are you sure you want to delete this condition?'}
                isLoading={isLoadingSave}
                onClose={() => {
                    popUpDeleteCondition.onClose();
                }}
            />
        </Stack>
    );
}
